import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

const HistoricalCultural = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = ["/images/Hist1.jpeg", "/images/Hist2.jpeg"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const itinerary = [
    {
      time: "08:30 AM",
      title: "Pick-Up",
      desc: "Pick-up from your hotel or the airport, followed by a short city orientation as we begin our heritage journey.",
      icon: "🚐",
    },
    {
      time: "09:00 AM",
      title: "Nairobi National Museum",
      desc: "Guided tour through galleries showcasing early man fossils, Kenya's birds, cultural heritage, and independence history. Optional Snake Park visit available.",
      icon: "🏛️",
    },
    {
      time: "01:30 PM",
      title: "Karen Blixen Museum",
      desc: "Step into the colonial farmhouse of author Karen Blixen, famed for Out of Africa. Learn about coffee farming and life in early 20th-century Kenya.",
      icon: "🏡",
    },
    {
      time: "04:00 PM",
      title: "Bomas of Kenya",
      desc: "Explore authentic traditional homesteads representing Kenya's diverse ethnic communities. Enjoy live cultural performances featuring music, dance, acrobatics, and storytelling.",
      icon: "🎭",
    },
    {
      time: "06:00 PM",
      title: "Drop-Off",
      desc: "Comfortable drop-off at your hotel or the airport — full of stories and memories from Kenya's rich past and living traditions.",
      icon: "🌅",
    },
  ];

  const highlights = [
    { icon: "🦴", title: "Early Man Fossils", desc: "Trace human origins through world-class paleontological exhibits." },
    { icon: "☕", title: "Colonial History", desc: "Relive the colonial era that shaped modern Kenya." },
    { icon: "🥁", title: "Living Culture", desc: "Experience authentic tribal performances and traditions." },
    { icon: "📜", title: "Independence Story", desc: "Understand the journey to Kenya's sovereignty." },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
          --charcoal: #141414;
        }

        .hc-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* ── HERO ── */
        .hc-hero {
          position: relative;
          min-height: 88vh;
          display: flex;
          align-items: flex-end;
          overflow: hidden;
        }
        .hc-hero-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          transition: opacity 1.8s ease-in-out;
        }
        .hc-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(10,10,10,1) 0%,
            rgba(10,10,10,0.55) 45%,
            rgba(10,10,10,0.25) 100%
          );
        }
        .hc-hero-content {
          position: relative;
          z-index: 2;
          padding: 4rem 1.5rem 5rem;
          max-width: 860px;
          animation: fadeUp 1.2s ease both;
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(32px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .hc-eyebrow {
          font-size: 0.68rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 1rem;
        }
        .hc-hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.8rem, 7vw, 5.5rem);
          font-weight: 700;
          line-height: 1.05;
          color: #fff;
          margin-bottom: 0.4rem;
        }
        .hc-hero-title em { font-style: italic; color: var(--gold-light); }
        .hc-hero-divider {
          width: 55px;
          height: 2px;
          background: var(--gold);
          margin: 1.4rem 0;
        }
        .hc-hero-desc {
          font-size: clamp(0.95rem, 2vw, 1.1rem);
          font-weight: 300;
          color: rgba(255,255,255,0.78);
          line-height: 1.9;
          max-width: 620px;
          margin-bottom: 2.2rem;
        }
        .hc-hero-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 2.5rem;
        }
        .hc-pill {
          border: 1px solid rgba(201,168,76,0.45);
          color: var(--gold-light);
          padding: 0.3rem 1rem;
          border-radius: 50px;
          font-size: 0.72rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 500;
        }
        .hc-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--gold);
          color: #0A0A0A;
          border: none;
          padding: 0.9rem 2.6rem;
          font-family: 'Jost', sans-serif;
          font-weight: 600;
          font-size: 0.82rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          border-radius: 3px;
          cursor: pointer;
          transition: background 0.3s, transform 0.2s;
          text-decoration: none;
        }
        .hc-btn:hover { background: var(--gold-light); transform: translateY(-2px); color: #0A0A0A; }

        /* ── STATS ── */
        .hc-stats {
          background: var(--charcoal);
          border-top: 1px solid rgba(201,168,76,0.2);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 1.8rem 1rem;
        }
        .hc-stat { text-align: center; padding: 0.4rem 1rem; }
        .hc-stat-n {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.2rem;
          font-weight: 700;
          color: var(--gold);
          line-height: 1;
        }
        .hc-stat-l {
          font-size: 0.65rem;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-top: 0.2rem;
        }

        /* ── GALLERY ── */
        .hc-gallery {
          padding: 5rem 0 4rem;
          background: var(--dark);
        }
        .hc-label {
          font-size: 0.65rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.6rem;
        }
        .hc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.4rem;
        }
        .hc-line {
          width: 40px;
          height: 2px;
          background: var(--gold);
          margin-bottom: 2.5rem;
        }
        .hc-img-frame {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 16/9;
          max-width: 760px;
          margin: 0 auto;
          box-shadow: 0 30px 80px rgba(0,0,0,0.6);
          border: 1px solid rgba(201,168,76,0.15);
        }
        .hc-img-frame img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: opacity 1.4s ease-in-out;
        }
        .hc-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.4rem;
        }
        .hc-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.25);
          border: none;
          cursor: pointer;
          transition: background 0.3s, transform 0.3s;
          padding: 0;
        }
        .hc-dot.active { background: var(--gold); transform: scale(1.4); }

        /* ── HIGHLIGHTS GRID ── */
        .hc-highlights {
          background: var(--charcoal);
          border-top: 1px solid rgba(201,168,76,0.12);
          padding: 5rem 0;
        }
        .hc-highlight-card {
          background: rgba(201,168,76,0.05);
          border: 1px solid rgba(201,168,76,0.15);
          border-radius: 8px;
          padding: 2rem 1.5rem;
          text-align: center;
          height: 100%;
          transition: border-color 0.3s, transform 0.3s;
        }
        .hc-highlight-card:hover {
          border-color: rgba(201,168,76,0.5);
          transform: translateY(-5px);
        }
        .hc-highlight-icon {
          font-size: 2.2rem;
          display: block;
          margin-bottom: 0.9rem;
        }
        .hc-highlight-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--gold-light);
          margin-bottom: 0.4rem;
        }
        .hc-highlight-desc {
          font-size: 0.82rem;
          color: rgba(255,255,255,0.5);
          line-height: 1.65;
        }

        /* ── ITINERARY ── */
        .hc-itinerary {
          background: var(--dark);
          padding: 5rem 0 6rem;
          border-top: 1px solid rgba(201,168,76,0.12);
        }
        .hc-timeline {
          max-width: 720px;
          margin: 0 auto;
          position: relative;
        }
        .hc-tl-item {
          display: flex;
          gap: 1.8rem;
          margin-bottom: 2rem;
        }
        .hc-tl-item:last-child { margin-bottom: 0; }
        .hc-tl-dot {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(201,168,76,0.08);
          border: 1px solid rgba(201,168,76,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 1.3rem;
          z-index: 1;
        }
        .hc-tl-body {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.1);
          border-radius: 6px;
          padding: 1.2rem 1.5rem;
          flex: 1;
          transition: border-color 0.3s;
        }
        .hc-tl-body:hover { border-color: rgba(201,168,76,0.4); }
        .hc-tl-time {
          font-size: 0.62rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.3rem;
        }
        .hc-tl-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.3rem;
        }
        .hc-tl-desc {
          font-size: 0.85rem;
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          line-height: 1.75;
        }

        /* ── CTA ── */
        .hc-cta {
          background: linear-gradient(135deg, #1a1200 0%, #2a1f00 50%, #1a1200 100%);
          border-top: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem;
          text-align: center;
        }
        .hc-cta h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 1rem;
        }
        .hc-cta p {
          font-size: 0.92rem;
          color: rgba(255,255,255,0.55);
          max-width: 480px;
          margin: 0 auto 2.5rem;
          line-height: 1.85;
        }

        @media (max-width: 576px) {
          .hc-hero-content { padding: 3rem 1.2rem 4rem; }
          .hc-timeline::before { left: 18px; }
          .hc-tl-dot { width: 36px; height: 36px; font-size: 1.1rem; }
        }
      `}</style>

      <div className="hc-root">

        {/* HERO */}
        <section className="hc-hero">
          {images.map((img, i) => (
            <div
              key={i}
              className="hc-hero-bg"
              style={{ backgroundImage: `url(${img})`, opacity: i === currentImage ? 1 : 0 }}
            />
          ))}
          <div className="hc-hero-overlay" />
          <div className="container">
            <div className="hc-hero-content">
              <p className="hc-eyebrow">Nairobi · Kenya</p>
              <h1 className="hc-hero-title">
                Historical &amp; <em>Cultural</em><br />Tour
              </h1>
              <div className="hc-hero-divider" />
              <p className="hc-hero-desc">
                Journey through Kenya's fascinating past and vibrant living traditions.
                From ancient fossils to colonial farmhouses to thundering tribal performances —
                this is heritage, alive and breathing.
              </p>
              <div className="hc-hero-pills">
                <span className="hc-pill">Full Day</span>
                <span className="hc-pill">$80 Per Person</span>
                <span className="hc-pill">3 Iconic Sites</span>
                <span className="hc-pill">Expert Guides</span>
              </div>
              <button className="hc-btn" onClick={() => window.location.href = "/tours"}>
                Book This Tour
              </button>
            </div>
          </div>
        </section>

        {/* STATS */}
        <div className="hc-stats">
          <div className="container">
            <div className="row justify-content-center">
              {[
                { n: "3", l: "Iconic Sites" },
                { n: "7–8", l: "Hours" },
                { n: "$80", l: "Per Person" },
                { n: "5★", l: "Rated" },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="hc-stat">
                    <div className="hc-stat-n">{s.n}</div>
                    <div className="hc-stat-l">{s.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GALLERY */}
        <section className="hc-gallery">
          <div className="container">
            <p className="hc-label">Experience Gallery</p>
            <h2 className="hc-title">Kenya's Heritage</h2>
            <div className="hc-line" />
            <div className="hc-img-frame">
              {images.map((img, i) => (
                <img key={i} src={img} alt={`Historical Tour ${i + 1}`} style={{ opacity: i === currentImage ? 1 : 0 }} />
              ))}
            </div>
            <div className="hc-dots">
              {images.map((_, i) => (
                <button key={i} className={`hc-dot ${i === currentImage ? "active" : ""}`} onClick={() => setCurrentImage(i)} />
              ))}
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="hc-highlights">
          <div className="container">
            <p className="hc-label" style={{ textAlign: "center" }}>What You'll Experience</p>
            <h2 className="hc-title" style={{ textAlign: "center" }}>Tour Highlights</h2>
            <div className="hc-line" style={{ margin: "0 auto 2.5rem" }} />
            <div className="row g-4">
              {highlights.map((h, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="hc-highlight-card">
                    <span className="hc-highlight-icon">{h.icon}</span>
                    <div className="hc-highlight-title">{h.title}</div>
                    <div className="hc-highlight-desc">{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ITINERARY */}
        <section className="hc-itinerary">
          <div className="container">
            <p className="hc-label" style={{ textAlign: "center" }}>Your Journey</p>
            <h2 className="hc-title" style={{ textAlign: "center" }}>Full Itinerary</h2>
            <div className="hc-line" style={{ margin: "0 auto 3rem" }} />
            <div className="hc-timeline">
              {itinerary.map((item, i) => (
                <div key={i} className="hc-tl-item">
                  <div className="hc-tl-dot">{item.icon}</div>
                  <div className="hc-tl-body">
                    <div className="hc-tl-time">{item.time}</div>
                    <div className="hc-tl-title">{item.title}</div>
                    <div className="hc-tl-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="hc-cta">
          <h2>Explore Kenya's Heritage</h2>
          <p>
            A perfect experience for history lovers, culture enthusiasts, and curious travellers.
            Book your immersive day today.
          </p>
          <button className="hc-btn" onClick={() => window.location.href = "/tours"}>
            Book Your Experience
          </button>
        </section>

      </div>
    </>
  );
};

export default HistoricalCultural;
